import React, { useEffect, useState } from 'react';
import {
   Datagrid,
   DateField,
   List,
   TextField,
   FunctionField,
   TextInput,
   WithRecord,
   SelectArrayInput,
} from 'react-admin';
import { Tooltip } from '@mui/material';
import {
   limitedArrayField,
   setChoices,
   reportTypes
} from '../../../../helpers';
import { TablePagination } from '../../../helperComponents/TablePagination';
import { TitleAboveTable } from '../../../helperComponents/TitleAboveTable';
import { httpClient } from '../../../../config';
import { BulkUpdateStatus } from './helpers/BulkUpdateStatus';
import { useRowStyle } from '../../../../hooks/useRowStyle';
import { ToolBarRowStyleToggle } from '../../../helperComponents/ToolBarRowStyleToggle';
import { getStatusInfo } from '../../../../utils/reportUtils';
import { REPORT_TYPES } from '../../../../config/constants/reportConstants';

export const ReportSettingsList = props => {
   const [userNames, setUserNames] = useState([]);
   const [affiliatesHashMap, setAffiliatesHashMap] = useState(new Map());
   const [mediaSourcesMap, setMediaSourcesMap] = useState(new Map());
   const [accountsHashMap, setAccountsHashMap] = useState(new Map());

   const rowClassName = useRowStyle();

   useEffect(() => {
      try {
         const doRequest = async () => {
            const userNamesResponse = await httpClient('/tables/reference_fields/user_names');
            const affiliatesResponse = await httpClient('/tables/reference_fields/affiliate_names');
            const accountsResponse = await httpClient('/tables/reference_fields/platform_account_info');
            const mediaSourcesResponse = await httpClient('/tables/reference_fields/media_sources');

            const fetchedUsers = JSON.parse(userNamesResponse.body) || [];
            const fetchedAffiliates = JSON.parse(affiliatesResponse.body) || [];
            const fetchedMediaSources = JSON.parse(mediaSourcesResponse.body) || [];
            const fetchedAccounts = JSON.parse(accountsResponse.body) || [];

            const initialAffiliatesMap = new Map();
            const initialAccountsMap = new Map();
            const initialMediaSourcesMap = new Map();

            fetchedAffiliates.forEach(({id, affiliate}) => initialAffiliatesMap.set(id, affiliate));
            fetchedMediaSources.forEach(({id, media_source}) => initialMediaSourcesMap.set(id, media_source));
            fetchedAccounts.forEach(({id, account_name}) => initialAccountsMap.set(id, account_name));

            setUserNames(fetchedUsers);
            setAffiliatesHashMap(initialAffiliatesMap);
            setMediaSourcesMap(initialMediaSourcesMap);
            setAccountsHashMap(initialAccountsMap);
         };

         doRequest();
      } catch (err) {
         console.log(err);
      }
   }, []);

   return (
     <>
        <TitleAboveTable title="Report definitions"/>
        <List {...props}
              title=" "
              actions={<ToolBarRowStyleToggle/>}
              pagination={<TablePagination/>}
              filters={[
                 <TextInput source="q"
                            label="ID, Report name, Affiliate ID, Status, Internal Account ID, User access ID"
                            alwaysOn
                            sx={{
                               width: '600px',
                            }}
                 />,
                 <SelectArrayInput source="report_types_filter"
                                   label="Report types"
                                   choices={setChoices(reportTypes)}
                 />,
                 <SelectArrayInput source="user_names_filter"
                                   label="User names"
                                   choices={userNames.map(({id, username}) => ({id, name: username}))}
                 />,
              ]}
              perPage={25}
        >
           <Datagrid rowClick="edit" bulkActionButtons={<BulkUpdateStatus/>} className={`${rowClassName} datagrid-content`}>
              <TextField source="id" label="ID"/>
              <TextField source="report_name"/>
              <TextField source="timezone"/>
              <FunctionField
                label="Exclude Rejected Sales"
                render={({ settings, report_type }) =>
                  [REPORT_TYPES.META_MEDIA_APPS_FLYER_MASTER, REPORT_TYPES.GOOGLE_MEDIA_APPS_FLYER_MASTER, REPORT_TYPES.GOOGLE_UAC_APPS_FLYER_MASTER].includes(report_type)
                    ? '-'
                    : settings?.exclude_rejected_sales ? '✓' : '✗'
                }
                textAlign="center"
              />
              <WithRecord
                label="Affiliate IDs"
                render={({affiliate_ids}) => (
                  <Tooltip title={
                    affiliate_ids && affiliate_ids.map(affiliateId => (
                      <div>{`${affiliateId} | ${affiliatesHashMap.get(affiliateId) || 'Not found'}`}</div>
                    ))
                  } placement="left-start">
                     <div>
                        <FunctionField
                          render={({affiliate_ids}) => limitedArrayField(affiliate_ids, 3)}
                        />
                     </div>
                  </Tooltip>
                )}
              />
               <WithRecord
                   label="Media sources"
                   render={({media_sources}) => (
                       <Tooltip title={
                           media_sources && media_sources.map(media_source => (
                               <div>{`${media_source} | ${mediaSourcesMap.get(media_source) || 'Not found'}`}</div>
                           ))
                       } placement="left-start">
                           <div>
                               <FunctionField
                                   render={({media_sources}) => limitedArrayField(media_sources, 3)}
                               />
                           </div>
                       </Tooltip>
                   )}
               />
              <FunctionField
                label="Status"
                sortBy="status"
                render={({ status }) => getStatusInfo(status).label}
              />
              <TextField source="report_type"/>
              <WithRecord
                label="Internal Accounts IDs"
                render={({internal_account_ids}) => (
                  <Tooltip title={
                    internal_account_ids && internal_account_ids.map(accountId => (
                      <div>{`${accountId} | ${accountsHashMap.get(accountId) || 'Not found'}`}</div>
                    ))
                  } placement="left-start">
                     <div>
                        <FunctionField
                          render={({internal_account_ids}) => limitedArrayField(internal_account_ids, 3)}
                        />
                     </div>
                  </Tooltip>
                )}
              />
              <DateField source="default_start_date"/>
              <TextField source="goal_names" label="Goal names" emptyText="All"/>
              <TextField source="event_names" label="Event names" emptyText="All"/>
           </Datagrid>
        </List>
     </>
   );
};
